import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
//dnd-kit
import {
  Tabs,
  Button,
  Drawer,
  Input,
  message,
  Spin,
  Empty,
  Card,
  Space,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  HolderOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PageBreadcrumbs from '../../../components/common/PageBreadcrumbs';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../config/permissions';
import productService from '../../../services/productService';
import attributeCatalogService from '../../../services/attributeCatalogService';
import { ATTRIBUTE_CATEGORIES } from '../../../config/attributeCategories';

const LIST_TYPE_CATEGORIES = ['Risks', 'Reasons to Invest'];

function normalizeCategory(cat) {
  if (!cat) return '';
  const c = String(cat).trim();
  if (c.toLowerCase() === 'general') return 'Overview Details';
  return c;
}

function getAttributesByCategory(attributes, categoryValue) {
  const normalized = categoryValue === 'description' ? 'description' : categoryValue;
  return attributes.filter((a) => {
    const cat = normalizeCategory(a.category);
    return cat === normalized || (normalized === 'Overview Details' && cat === 'General');
  });
}

function SortableAttributeRow({ item, onRemove, onValueChange, isListType, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id ?? item.attribute_id ?? item.key_name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  let listValue = [];
  try {
    listValue = typeof item.value === 'string' ? JSON.parse(item.value || '[]') : item.value || [];
  } catch {
    listValue = Array.isArray(item.value) ? item.value : [];
  }
  if (!Array.isArray(listValue)) listValue = [];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 group hover:border-gray-300"
    >
      {!disabled && (
        <span
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 shrink-0 mt-0.5"
          {...listeners}
        >
          <HolderOutlined />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-800">{item.display_label || item.key_name}</div>
        {!isListType ? (
          <Input
            value={item.value ?? ''}
            onChange={(e) => onValueChange?.(item, e.target.value)}
            placeholder="Value"
            className="mt-1"
            disabled={disabled}
          />
        ) : (
          <div className="mt-2 space-y-2">
            {listValue.map((entry, idx) => (
              <Card key={idx} size="small" className="bg-gray-50 border-gray-200">
                <Input
                  placeholder="Heading"
                  value={entry.heading ?? ''}
                  onChange={(e) => {
                    const next = listValue.map((x, i) => (i === idx ? { ...x, heading: e.target.value } : x));
                    onValueChange?.(item, JSON.stringify(next));
                  }}
                  className="font-semibold mb-1"
                  disabled={disabled}
                />
                <Input.TextArea
                  placeholder="Description"
                  value={entry.description ?? ''}
                  onChange={(e) => {
                    const next = listValue.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x));
                    onValueChange?.(item, JSON.stringify(next));
                  }}
                  rows={2}
                  disabled={disabled}
                />
              </Card>
            ))}
            {!disabled && (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  const next = [...listValue, { heading: '', description: '' }];
                  onValueChange?.(item, JSON.stringify(next));
                }}
              >
                Add item
              </Button>
            )}
          </div>
        )}
      </div>
      {!disabled && (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => onRemove(item)}
          className="shrink-0 opacity-0 group-hover:opacity-100"
        />
      )}
    </div>
  );
}

export default function ProductAttributeMappingPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canEditMapping = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCTS_EDIT);
  const canCreateCatalogAttribute = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCTS_CREATE);
  const [product, setProduct] = useState(null);
  const [attributes, setAttributes] = useState([]);
  const [catalogAttributes, setCatalogAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCategory, setSavingCategory] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(ATTRIBUTE_CATEGORIES[0]?.value ?? 'description');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const productTypeId = product?.product_type_id ?? '1';

  const fetchProductAndAttributes = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const prodRes = await productService.getProduct(productId);
      const raw = prodRes?.data;
      const productData = raw?.product ?? raw;
      if (productData) setProduct(productData);
      // Use numeric id from record for attributes API (backend expects id, not uuid)
      const numericId = productData?.record_id ?? productData?.id ?? productId;
      const attrRes = await productService.getProductAttributes(numericId);
      if (attrRes?.success && attrRes?.data?.attributes) {
        setAttributes(attrRes.data.attributes.map((a) => ({ ...a })));
      } else {
        setAttributes([]);
      }
    } catch (e) {
      message.error('Failed to load product or attributes');
      console.error(e);
      setAttributes([]);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const fetchCatalog = useCallback(async () => {
    try {
      const result = await attributeCatalogService.getAttributes({ type: 'PRE_IPO' });
      const list = result?.success ? result.data : [];
      setCatalogAttributes(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setCatalogAttributes([]);
    }
  }, []);

  useEffect(() => {
    fetchProductAndAttributes();
  }, [fetchProductAndAttributes]);

  useEffect(() => {
    if (drawerOpen) fetchCatalog();
  }, [drawerOpen, fetchCatalog]);

  const mappedKeys = new Set(attributes.map((a) => (a.key_name || a.key || '').toLowerCase()));
  const unmappedCatalog = catalogAttributes.filter(
    (c) => !mappedKeys.has((c.key || c.key_name || '').toLowerCase())
  );

  const getTabAttributes = (categoryValue) => {
    const list = getAttributesByCategory(attributes, categoryValue);
    return list.sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  };

  const handleDragEnd = (event, categoryValue) => {
    if (!canEditMapping) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const list = getTabAttributes(categoryValue);
    const oldIndex = list.findIndex((i) => (i.id ?? i.attribute_id ?? i.key_name) === active.id);
    const newIndex = list.findIndex((i) => (i.id ?? i.attribute_id ?? i.key_name) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(list, oldIndex, newIndex).map((a, i) => ({ ...a, order_index: i }));
    const tabIds = new Set(reordered.map((a) => a.id ?? a.attribute_id ?? a.key_name));
    setAttributes((prev) => {
      const others = prev.filter((a) => !tabIds.has(a.id ?? a.attribute_id ?? a.key_name));
      return [...others, ...reordered];
    });
  };

  const addAttributeToProduct = (catalogAttr) => {
    if (!canEditMapping) return;
    const category = normalizeCategory(catalogAttr.category) || activeTab;
    const maxOrder = Math.max(0, ...getTabAttributes(category).map((a) => a.order_index ?? 0));
    const newAttr = {
      id: undefined,
      attribute_id: catalogAttr.id,
      key_name: catalogAttr.key || catalogAttr.key_name,
      display_label: catalogAttr.label || catalogAttr.display_label,
      value_type: catalogAttr.data_type || catalogAttr.value_type || 'string',
      value: catalogAttr.value ?? null,
      order_index: maxOrder + 1,
      category: category,
      enum_values: catalogAttr.enum_values,
    };
    setAttributes((prev) => [...prev, newAttr]);
  };

  const handleValueChange = (item, newValue) => {
    setAttributes((prev) =>
      prev.map((a) =>
        (a.id ?? a.attribute_id ?? a.key_name) === (item.id ?? item.attribute_id ?? item.key_name)
          ? { ...a, value: newValue }
          : a
      )
    );
  };

  const removeAttributeFromProduct = (item) => {
    setAttributes((prev) => prev.filter((a) => (a.id ?? a.attribute_id ?? a.key_name) !== (item.id ?? item.attribute_id ?? item.key_name)));
  };

  const buildAttributePayloadForCategory = (categoryValue) => {
    const isListType = LIST_TYPE_CATEGORIES.includes(categoryValue);
    const attrs = {};
    getTabAttributes(categoryValue).forEach((a) => {
      const key = a.key_name || a.key;
      if (!key) return;
      let value = a.value;
      if (isListType && value != null && value !== '') {
        try {
          value = typeof value === 'string' ? JSON.parse(value) : value;
          if (!Array.isArray(value)) value = [value];
        } catch {
          value = Array.isArray(value) ? value : [];
        }
      } else if (value != null && value !== '') {
        value = String(value);
      } else {
        value = null;
      }
      attrs[key] = value;
    });
    return attrs;
  };

  const handleSaveCategory = async (categoryValue) => {
    if (!canEditMapping) return;
    setSavingCategory(categoryValue);
    try {
      const pid = product?.uuid ?? product?.id ?? productId;
      const attrs = buildAttributePayloadForCategory(categoryValue);
      const result = await productService.updateProduct(pid, { attributes: attrs });
      if (result?.success !== false) {
        message.success(`${ATTRIBUTE_CATEGORIES.find((c) => c.value === categoryValue)?.label ?? categoryValue} saved`);
        fetchProductAndAttributes();
      } else {
        message.error(result?.message || 'Failed to save attributes');
      }
    } catch (e) {
      message.error('Failed to save attributes');
      console.error(e);
    } finally {
      setSavingCategory(null);
    }
  };

  if (loading && !product) {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden min-h-0 flex flex-col flex-1 h-full">
        <div className="shrink-0"><PageBreadcrumbs /></div>
        <div className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center p-8">
          <Spin size="large" tip="Loading…" />
        </div>
      </div>
    );
  }

  const tabItems = ATTRIBUTE_CATEGORIES.map((cat) => {
    const tabAttrs = getTabAttributes(cat.value);
    const isListType = LIST_TYPE_CATEGORIES.includes(cat.value);

    return {
      key: cat.value,
      label: (
        <span className="flex items-center gap-1">
          {cat.value === 'Risks' && <ExclamationCircleOutlined />}
          {cat.value === 'Reasons to Invest' && <StarOutlined />}
          {cat.label}
        </span>
      ),
      children: (
        <div className="py-4">
          {canEditMapping && (
            <div className="flex justify-end items-center gap-2 mb-3">
              <Button type="default" icon={<PlusOutlined />} onClick={() => setDrawerOpen(true)}>
                Add from catalog
              </Button>
              <Button
                type="primary"
                loading={savingCategory === cat.value}
                onClick={() => handleSaveCategory(cat.value)}
              >
                Save {cat.label}
              </Button>
            </div>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => handleDragEnd(e, cat.value)}
          >
            <SortableContext
              items={tabAttrs.map((a) => a.id ?? a.attribute_id ?? a.key_name)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {tabAttrs.length === 0 ? (
                  <Empty
                    description={
                      canEditMapping
                        ? 'No attributes in this category. Add from catalog.'
                        : 'No attributes in this category.'
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    {canEditMapping && (
                      <Button type="primary" onClick={() => setDrawerOpen(true)}>
                        Add from catalog
                      </Button>
                    )}
                  </Empty>
                ) : (
                  tabAttrs.map((item) => (
                    <SortableAttributeRow
                      key={item.id ?? item.attribute_id ?? item.key_name}
                      item={item}
                      onRemove={removeAttributeFromProduct}
                      onValueChange={handleValueChange}
                      isListType={isListType}
                      disabled={!canEditMapping}
                    />
                  ))
                )}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ),
    };
  });

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden min-h-0 flex flex-col flex-1 h-full">
      <div className="shrink-0">
        <PageBreadcrumbs />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <Space className="flex-wrap">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/pre-ipo/listings')}
            >
              Back to Listings
            </Button>
            <span className="text-base text-gray-900">
              <span className="font-semibold">Attribute Mapping</span>
              {product?.name && (
                <span className="text-gray-600 font-normal ml-1.5">· {product.name}</span>
              )}
            </span>
          </Space>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="attribute-mapping-tabs"
        />

        {canEditMapping && (
          <Drawer
            title="Add attribute from catalog"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            width={400}
            footer={null}
          >
            <p className="text-gray-500 text-sm mb-3">
              Attributes not yet mapped to this product. Click to add to current category.
            </p>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {unmappedCatalog.length === 0 ? (
                <Empty
                  description="All catalog attributes are already mapped, or none exist yet."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  {canCreateCatalogAttribute && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setDrawerOpen(false);
                        navigate('/pre-ipo/attribute-catalog', {
                          state: { openCreate: true, category: activeTab },
                        });
                      }}
                    >
                      Create new attribute
                    </Button>
                  )}
                </Empty>
              ) : (
                unmappedCatalog.map((c) => (
                  <Card
                    key={c.id ?? c.key}
                    size="small"
                    className="cursor-pointer hover:border-green-400 hover:shadow-sm transition-all"
                    onClick={() => {
                      addAttributeToProduct(c);
                      setDrawerOpen(false);
                    }}
                  >
                    <div className="font-medium text-gray-800">{c.label || c.key}</div>
                    <div className="text-xs text-gray-500">{c.key || c.key_name} · {c.category}</div>
                  </Card>
                ))
              )}
            </div>
          </Drawer>
        )}
      </div>
    </div>
  );
}
